import styled from "styled-components";

const FONT_BODY = '"Manrope", "IBM Plex Sans", "Avenir Next", sans-serif';
const FONT_DISPLAY = '"Space Grotesk", "IBM Plex Sans", "Avenir Next", sans-serif';
const FONT_EDITORIAL = '"DM Serif Text", "Bodoni Moda", serif';

export const PageRoot = styled.main`
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  color: #224b3c;
  font-family: ${FONT_BODY};
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  background:
    radial-gradient(circle at 10% 12%, rgba(170, 231, 203, 0.34) 0%, rgba(170, 231, 203, 0) 38%),
    radial-gradient(circle at 88% 14%, rgba(118, 211, 182, 0.3) 0%, rgba(118, 211, 182, 0) 34%),
    linear-gradient(180deg, #f7fdf9 0%, #edf8f2 50%, #f6fcf8 100%);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    opacity: 0.22;
    background: repeating-linear-gradient(
      90deg,
      rgba(56, 121, 95, 0.11) 0,
      rgba(56, 121, 95, 0.11) 1px,
      transparent 1px,
      transparent 92px
    );
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

export const SectionContainer = styled.div`
  width: min(1280px, calc(100% - 48px));
  margin: 0 auto;
  display: grid;
  grid-template-columns: ${(props) => (props.vertical ? "1fr" : "0.96fr 1.04fr")};
  gap: ${(props) => (props.vertical ? "30px" : "60px")};

  @media (max-width: 992px) {
    width: min(1280px, calc(100% - 34px));
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;

export const HeroSection = styled.section`
  padding: clamp(148px, 20vw, 212px) 0 clamp(82px, 10vw, 118px);
  border-top: 1px solid rgba(90, 168, 138, 0.2);
  border-bottom: 1px solid rgba(90, 168, 138, 0.2);
  background:
    linear-gradient(
      130deg,
      rgba(6, 20, 14, 0.48) 0%,
      rgba(8, 24, 17, 0.42) 52%,
      rgba(6, 20, 14, 0.5) 100%
    ),
    url("/images/fundo verde.jpg") no-repeat center center / cover;
`;

export const HeroEyebrow = styled.div`
  width: fit-content;
  min-height: 32px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.56);
  background: rgba(10, 31, 21, 0.34);
  backdrop-filter: blur(10px);
  font-family: ${FONT_DISPLAY};
  color: #eff8f2;
  font-size: 0.69rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
`;

export const HeroTitle = styled.h1`
  margin: 6px 0 0;
  max-width: 13ch;
  font-family: ${FONT_DISPLAY};
  color: #f3faf6;
  font-size: clamp(2.3rem, 6vw, 5.1rem);
  line-height: 0.92;
  letter-spacing: -0.03em;
  text-wrap: balance;
`;

export const HeroText = styled.p`
  margin: 0;
  max-width: 64ch;
  color: rgba(233, 244, 237, 0.92);
  font-size: clamp(1rem, 1.32vw, 1.08rem);
  line-height: 1.84;
`;

export const ResultsSection = styled.section`
  padding: clamp(84px, 9vw, 116px) 0;
`;

export const SectionEyebrow = styled.div`
  width: fit-content;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(90, 168, 138, 0.28);
  background: rgba(255, 255, 255, 0.64);
  backdrop-filter: blur(8px);
  font-family: ${FONT_DISPLAY};
  color: #346a52;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
`;

export const SectionTitle = styled.h2`
  margin: 8px 0 8px;
  font-family: ${FONT_EDITORIAL};
  color: #1f5b43;
  font-size: clamp(2rem, 4.4vw, 3.6rem);
  line-height: 1.05;
  letter-spacing: -0.02em;
  max-width: 15ch;
  text-wrap: balance;
`;

export const SectionText = styled.p`
  margin: 0;
  max-width: 68ch;
  color: #4f725f;
  font-size: clamp(0.99rem, 1.26vw, 1.08rem);
  line-height: 1.84;
`;

export const ResultsGrid = styled.div`
  margin-top: 30px;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const ResultCard = styled.article`
  grid-column: span 6;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.66);
  box-shadow:
    0 18px 36px rgba(62, 127, 101, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);
  padding: 18px;
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

  :hover {
    transform: translateY(-4px);
    border-color: rgba(166, 228, 199, 0.72);
    box-shadow:
      0 24px 40px rgba(62, 127, 101, 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  @media (max-width: 1180px) {
    grid-column: span 1;
  }
`;

export const ResultHeader = styled.header`
  min-height: 72px;
`;

export const ResultTitle = styled.h3`
  margin: 0;
  color: #235f46;
  font-family: ${FONT_DISPLAY};
  font-size: 1.02rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const ResultInfo = styled.p`
  margin: 5px 0 0;
  color: #597968;
  font-size: 0.88rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const CompareGrid = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.78);
  box-shadow: 0 10px 20px rgba(62, 127, 101, 0.14);
`;

export const CompareItem = styled.figure`
  margin: 0;
  position: relative;
  background: #eef5f0;
`;

export const CompareTrack = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.32s ease;
`;

export const CompareLabel = styled.figcaption`
  position: absolute;
  top: 10px;
  left: 10px;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: rgba(26, 68, 48, 0.44);
  backdrop-filter: blur(8px);
  color: #f3fff8;
  font-family: ${FONT_DISPLAY};
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const CompareImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

export const CompareControls = styled.div`
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: inline-flex;
  gap: 6px;
`;

export const CompareNav = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.64);
  background: rgba(20, 56, 40, 0.56);
  color: #f4fff8;
  font-family: ${FONT_DISPLAY};
  font-size: 0.78rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  :hover {
    background: rgba(20, 56, 40, 0.72);
    transform: translateY(-1px);
  }
`;

export const CompareDots = styled.div`
  min-height: 10px;
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const CompareDot = styled.button`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  border: 0;
  padding: 0;
  cursor: pointer;
  background: ${(props) => (props.$active ? "#2f7a5a" : "rgba(94, 140, 117, 0.36)")};
  transition: transform 0.2s ease, opacity 0.2s ease;

  :hover {
    transform: scale(1.08);
    opacity: 0.92;
  }
`;

export const ResultDescription = styled.p`
  margin: 12px 0 0;
  color: #4e6f5d;
  font-size: 0.96rem;
  line-height: 1.7;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const CtaSection = styled.section`
  padding: 10px 0 84px;
`;

export const CtaCard = styled.div`
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  background: linear-gradient(132deg, rgba(255, 255, 255, 0.72) 0%, rgba(229, 249, 239, 0.68) 100%);
  box-shadow:
    0 24px 46px rgba(62, 127, 101, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  padding: clamp(24px, 4vw, 40px);
`;

export const CtaTitle = styled.h3`
  margin: 0;
  max-width: 16ch;
  font-family: ${FONT_DISPLAY};
  color: #1f5b43;
  letter-spacing: -0.02em;
  line-height: 1.05;
  font-size: clamp(1.5rem, 3vw, 2.5rem);
`;

export const CtaText = styled.p`
  margin: 12px 0 0;
  max-width: 62ch;
  color: #4f715f;
  font-size: clamp(0.98rem, 1.24vw, 1.06rem);
  line-height: 1.76;
`;

export const CtaButton = styled.button`
  margin-top: 18px;
  min-height: 50px;
  padding: 0 22px;
  border: 1px solid rgba(67, 140, 110, 0.36);
  border-radius: 999px;
  background: linear-gradient(120deg, #3e8f6d 0%, #67bf98 100%);
  color: #f6fff9;
  font-family: ${FONT_DISPLAY};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 14px 28px rgba(62, 127, 101, 0.26);
  transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease;

  :hover {
    transform: translateY(-2px);
    filter: brightness(0.97);
    box-shadow: 0 18px 32px rgba(62, 127, 101, 0.32);
  }
`;
